import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Save, Info } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";
import { HistoryButton } from "./HistoryDialog";
import { MessageTemplates } from "./MessageTemplates";

const STATUS_COLORS = {
    Neu: "bg-blue-100 text-blue-800",
    "In Prüfung": "bg-amber-100 text-amber-800",
    Rückfrage: "bg-purple-100 text-purple-800",
    Eingeladen: "bg-emerald-100 text-emerald-800",
    Abgelehnt: "bg-red-100 text-red-800",
    Eingestellt: "bg-slate-800 text-white",
};

export const EmailTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingStatus, setSavingStatus] = useState(null);

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await apiClient.get("/admin/email-templates");
            setTemplates(data.items || []);
        } catch (err) {
            if (err?.response?.status !== 401) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Vorlagen konnten nicht geladen werden.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const updateField = (status, field, value) => {
        setTemplates((prev) => prev.map((t) => (t.status === status ? { ...t, [field]: value } : t)));
    };

    const saveTemplate = async (tpl) => {
        if (!tpl.subject?.trim() || !tpl.body?.trim()) {
            toast.error("Betreff und Text dürfen nicht leer sein.");
            return;
        }
        setSavingStatus(tpl.status);
        try {
            await apiClient.put(`/admin/email-templates/${encodeURIComponent(tpl.status)}`, {
                subject: tpl.subject,
                body: tpl.body,
            });
            toast.success(`Vorlage "${tpl.status}" gespeichert.`);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Speichern fehlgeschlagen.");
        } finally {
            setSavingStatus(null);
        }
    };

    return (
        <div data-testid="email-templates" className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 text-sm text-blue-900">
                <Info size={18} className="mt-0.5 shrink-0" />
                <div>
                    Diese Texte werden an Bewerber gesendet, wenn Sie beim Ändern eines Status die Option
                    {" "}<strong>„Bewerber per E-Mail benachrichtigen"</strong> aktivieren.
                    Verfügbare Platzhalter: <code className="bg-blue-100 px-1 rounded">{"{vorname}"}</code>,{" "}
                    <code className="bg-blue-100 px-1 rounded">{"{nachname}"}</code>,{" "}
                    <code className="bg-blue-100 px-1 rounded">{"{position}"}</code>
                </div>
            </div>

            {loading && (
                <div className="bg-white border border-moebus-border rounded-lg p-8 text-center text-slate-500">
                    Lade…
                </div>
            )}

            {!loading &&
                templates.map((tpl) => (
                    <div
                        key={tpl.status}
                        data-testid={`template-${tpl.status}`}
                        className="bg-white border border-moebus-border rounded-lg p-4 md:p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <span
                                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                    STATUS_COLORS[tpl.status] || "bg-slate-100 text-slate-700"
                                }`}
                            >
                                {tpl.status}
                            </span>
                            <div className="flex items-center gap-2">
                                <HistoryButton
                                    historyKey={`template_${tpl.status}`}
                                    onRestore={(v) =>
                                        setTemplates((prev) =>
                                            prev.map((t) =>
                                                t.status === tpl.status
                                                    ? { ...t, subject: v.subject || "", body: v.body || "" }
                                                    : t
                                            )
                                        )
                                    }
                                />
                                <button
                                    data-testid={`save-template-${tpl.status}`}
                                    onClick={() => saveTemplate(tpl)}
                                    disabled={savingStatus === tpl.status}
                                    className="inline-flex items-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-md text-sm disabled:opacity-60"
                                >
                                    <Save size={14} /> {savingStatus === tpl.status ? "Speichern…" : "Speichern"}
                                </button>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Betreff
                                </label>
                                <input
                                    data-testid={`template-subject-${tpl.status}`}
                                    type="text"
                                    value={tpl.subject || ""}
                                    onChange={(e) => updateField(tpl.status, "subject", e.target.value)}
                                    className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    E-Mail-Text
                                </label>
                                <textarea
                                    data-testid={`template-body-${tpl.status}`}
                                    value={tpl.body || ""}
                                    onChange={(e) => updateField(tpl.status, "body", e.target.value)}
                                    rows={7}
                                    className="w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green resize-y leading-relaxed"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            <MessageTemplates />
        </div>
    );
};
